// src/App.tsx
import React from "react";
import { runtimeEnvironment } from "./lib/runtimeEnvironment";
import { copyrightLine } from "./lib/copyrightLine";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { User } from "@supabase/supabase-js";
import { fetchWithAuth } from "./lib/api";
import {
  demoResetFailureMessage,
  isDemoResetRefusal,
} from "./lib/demoResetFailure";
import {
  ThemeProvider,
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  CircularProgress,
  Alert,
  Collapse,
  Tooltip,
  alpha,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import { TrendingUp as TrendingUpIcon } from "@mui/icons-material";
import ForumIcon from "@mui/icons-material/Forum";
import MessagesPage from "./components/messages/MessagesPage";
import { MarketInsightsPage } from "./components/MarketInsights";
import {
  AccountCircle as AccountIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Apartment as ApartmentIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  AutoAwesomeMotionRounded as ContentIcon,
  Email as EmailIcon,
  ChevronLeft as ChevronLeftIcon,
  Close as CloseIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from "@mui/icons-material";
import { supabase } from "./lib/supabase";
import { buildTheme, gradients, ThemeMode } from "./theme/theme";
import { activityService } from "./services/activityService";
import {
  emailService,
  type EmailConfigIdentity,
} from "./services/emailService";
import { clientStore } from "./lib/clientStorage";
import {
  DEVICE_THEME_KEY,
  activeUserPayloadMatchesGeneration,
  isActiveUserPayloadKey,
  LEGACY_LOCAL_DATA_KEYS,
  readRegisteredValue,
  serializeRegisteredValue,
  THEME_POLICY,
} from "./lib/persistenceRegistry";
import {
  clearUserPersistence,
  getActivePersistenceUser,
  getPersistenceGeneration,
  PERSISTENCE_LOCAL_CLEAR_KEY,
  readPersistenceEvent,
  transitionPersistenceAuth,
} from "./lib/persistenceIdentity";
import { walkthroughSessionStore } from "./lib/walkthroughSessionStore";
import {
  authSessionSignalReducer,
  roleLoadModeForAuthEvent,
} from "./lib/authSessionLifecycle";
import {
  buildSelectedPropertyNavigationState,
  clearUserNavigationPersistence,
  parseAppNavigationState,
  writeAppNavigationState,
  type ClientMainTab,
  type ClientNavigationStatus,
  applyPopStateNavigation,
} from "./lib/appNavigationState";
import Login from "./components/Login";
import ApartmentSearch from "./components/ApartmentSearch";
import ClientList from "./components/clients/ClientList";
import ApartmentDashboard from "./components/ApartmentDashboard";
import MobileBottomNav from "./components/MobileBottomNav";
import Settings from "./components/Settings";
import {
  DEFAULT_SETTINGS_TAB,
  type SettingsTabKey,
} from "./components/settings/settingsTabs";
import PropertiesShell from "./components/PropertiesShell";
import PropertiesPage from "./components/PropertiesPage";
import AdminDashboard from "./components/AdminDashboard";
import FloorplanPublicPage from "./components/FloorplanPublicPage";
import { WalkthroughProvider, useWalkthroughContext } from "./components/walkthrough/WalkthroughProvider";
import { WalkthroughOverlay } from "./components/walkthrough/WalkthroughOverlay";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StagingBanner from "./components/StagingBanner";
import SocialsRoute from "./components/socials/SocialsRoute";
import NotificationMenuState from "./components/notifications/NotificationMenuState";
import NotificationBellBadge from "./components/notifications/NotificationBellBadge";
import {
  canAccessAdminContent,
  canAccessAdminSurface,
} from "./lib/capabilities";
import {
  STALE_BUILD_EVENT,
  type StaleBuildSignal,
} from "./lib/staleClientGuard";
import { prepareUpdateReload } from "./lib/updateReloadPolicy";
import { subscribeAppResume } from "./lib/appResumeCoordinator";

interface DashboardSearchCriteria {
  min_price?: number | null;
  max_price?: number | null;
  selected_bedrooms?: string[];
  min_sqft?: number | null;
  available_only?: boolean;
  amenities?: string[];
  neighborhoods?: string[];
  price_per_sqft_max?: number | null;
  sort_by?: string;
  move_in_date?: string | null;
  selected_unit_ids?: string[];
}

// Base navigation items
const baseNavItems = [
  { id: "dashboard", label: "Dashboard", icon: <DashboardIcon /> },
  // Ch78 · the id stays "clients" (routing, deep links, persisted keys); the
  // LABEL is what the page now is — the Leads area. Real client work lives
  // in Messages (Grant's ruling 2026-09-07).
  { id: "clients", label: "Leads", icon: <PeopleIcon /> },
  { id: "properties", label: "Properties", icon: <ApartmentIcon /> },
  { id: "settings", label: "Settings", icon: <SettingsIcon /> },
];

// Tabs that live in the mobile bottom pill; the rest go to the More sheet.
// "messages" only exists in navItems for elevated roles, so agents/demo
// still get a 4-slot pill.
const PRIMARY_NAV_IDS = ["dashboard", "clients", "messages", "properties"];

// Light/dark themes built once; the user's pick persists in localStorage.
// "dark" stays the default (current look); "light" is the LocateNow brand
// blue/white that matches the client link + marketing site.
const themes: Record<ThemeMode, ReturnType<typeof buildTheme>> = {
  light: buildTheme("light"),
  dark: buildTheme("dark"),
};
const getStoredThemeMode = (): ThemeMode => {
  const raw = clientStore.getItem(DEVICE_THEME_KEY);
  const restored = readRegisteredValue<ThemeMode>(raw, THEME_POLICY);
  if (restored) return restored;
  // One-time migration from the pre-registry raw device preference.
  if (raw === "light" || raw === "dark") {
    const migrated = serializeRegisteredValue(raw, THEME_POLICY);
    if (migrated) clientStore.setItem(DEVICE_THEME_KEY, migrated);
    return raw;
  }
  if (raw) clientStore.removeItem(DEVICE_THEME_KEY);
  return "dark";
};
// Module-level fallback theme: pre-auth branches + breakpoints-only usages.
const theme = themes.dark;

// Brand mark the client-link /s/ page uses (white wordmark; sits on blue).
const COMPANY_LOGO_WHITE_URL =
  `${runtimeEnvironment.assetBaseUrl}/locatenow-logo-white.png`;

const DEMO_FALLBACK_EMAIL = "info@locatenowatl.com";
const CONTENT_REVIEW_ENABLED =
  process.env.REACT_APP_CONTENT_REVIEW_ENABLED === "true";
const isLocalhostHost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "127.0.0.1" ||
    window.location.hostname === "localhost");

const isStaleBuildSignal = (value: unknown): value is StaleBuildSignal => {
  if (!value || typeof value !== "object") return false;
  const signal = value as Partial<StaleBuildSignal>;
  return (
    typeof signal.startupSignature === "string" &&
    signal.startupSignature.length > 0 &&
    signal.startupSignature.length <= 4096 &&
    typeof signal.targetSignature === "string" &&
    signal.targetSignature.length > 0 &&
    signal.targetSignature.length <= 4096 &&
    typeof signal.detectedAt === "number" &&
    Number.isFinite(signal.detectedAt)
  );
};

const readCurrentNavigationState = () => {
  try {
    return parseAppNavigationState(window.location.search);
  } catch {
    return parseAppNavigationState("");
  }
};

const getUserDisplayName = (email: string) =>
  email
    .split("@")[0]
    .split(".")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

/** Demo-banner controls used for the replayable sandbox tour */
const DemoBannerTourButton: React.FC<{
  janeClientId: string | null;
  prepareWalkthrough?: () => Promise<void> | void;
}> = ({
  janeClientId,
  prepareWalkthrough,
}) => {
  const { startWalkthrough } = useWalkthroughContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const completed = walkthroughSessionStore.read().completed;
  const [preparing, setPreparing] = React.useState(false);
  if (isMobile) return null;

  const handleOpenIntro = async () => {
    if (preparing) return;

    setPreparing(true);
    try {
      if (prepareWalkthrough) {
        await prepareWalkthrough();
      }
    } catch (error) {
      console.warn(
        "Walkthrough prepare failed before intro, continuing anyway:",
        error
      );
    } finally {
      setPreparing(false);
    }

    startWalkthrough();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
      <Button
        size="small"
        variant="outlined"
        disabled={!janeClientId || preparing}
        startIcon={<PlayArrowIcon sx={{ fontSize: 16 }} />}
        onClick={handleOpenIntro}
        sx={{
          textTransform: "none", fontSize: "0.7rem", fontWeight: 600,
          borderColor: "rgba(245,158,11,0.4)", color: "#F59E0B",
          py: 0.25, px: 1.5, minHeight: 0,
          "&:hover": { borderColor: "#F59E0B", bgcolor: "rgba(245,158,11,0.08)" },
        }}
      >
        {preparing
          ? "Preparing Tour..."
          : completed
            ? "Replay Tour"
            : "Take a Tour"}
      </Button>
    </Box>
  );
};

// Public routes that skip the auth gate entirely.
// Recipients of shared floor plan links must be able to view without logging in.
const isPublicFloorplanRoute = (): boolean =>
  /^\/fp\/[^/?#]+/.test(window.location.pathname);

// Wrapper so we can short-circuit before the authed App component mounts
// (and before its hooks run). This keeps hook order stable and keeps the
// public page independent of auth/theme state.
const AppRoot: React.FC = () => {
  if (isPublicFloorplanRoute()) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <StagingBanner />
        <FloorplanPublicPage />
      </ThemeProvider>
    );
  }
  return (
    <>
      <StagingBanner />
      <App />
    </>
  );
};

const App: React.FC = () => {
  const [socialClientSource, setSocialClientSource] = React.useState<string | null>(null);
  const persistenceUserRef = React.useRef<string | null>(null);
  const authGenerationRef = React.useRef(0);
  const roleRequestEpochRef = React.useRef(0);
  const roleRequestAbortRef = React.useRef<AbortController | null>(null);
  const roleOwnerUserIdRef = React.useRef<string | null>(null);
  const queryClient = useQueryClient();
  const [user, setUser] = React.useState<User | null>(null);
  const [authSignal, dispatchAuthSignal] = React.useReducer(
    authSessionSignalReducer,
    null
  );
  // The signed-in user's profile photo (profiles.avatar_url) for the nav avatar.
  const [userAvatarUrl, setUserAvatarUrl] = React.useState<string | null>(null);
  // Appearance: light (brand blue/white) or dark. Shadows the module-level
  // fallback so every theme.* read below — and all three ThemeProviders in
  // this component — follow the toggle live.
  const [themeMode, setThemeMode] = React.useState<ThemeMode>(getStoredThemeMode);
  const [storageDurability, setStorageDurability] =
    React.useState(clientStore.durability);
  const [staleBuild, setStaleBuild] =
    React.useState<StaleBuildSignal | null>(null);
  const [updateReloading, setUpdateReloading] = React.useState(false);
  const [updateReloadError, setUpdateReloadError] =
    React.useState<string | null>(null);
  React.useEffect(
    () => clientStore.subscribeDurability(setStorageDurability),
    []
  );
  React.useEffect(() => {
    const handleStaleBuild = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail;
      if (!isStaleBuildSignal(detail)) return;
      setStaleBuild(detail);
      setUpdateReloadError(null);
    };
    window.addEventListener(STALE_BUILD_EVENT, handleStaleBuild);
    return () => window.removeEventListener(STALE_BUILD_EVENT, handleStaleBuild);
  }, []);
  const theme = themes[themeMode];
  const toggleThemeMode = () => {
    setThemeMode((prev) => {
      const next: ThemeMode = prev === "dark" ? "light" : "dark";
      const stored = serializeRegisteredValue(next, THEME_POLICY);
      if (stored) clientStore.setItem(DEVICE_THEME_KEY, stored);
      return next;
    });
  };
  const handleReloadUpdate = async () => {
    if (!staleBuild || updateReloading) return;
    setUpdateReloading(true);
    setUpdateReloadError(null);
    try {
      const decision = await prepareUpdateReload(staleBuild.targetSignature, {
        commitReload: () => window.location.reload(),
      });
      switch (decision.status) {
        case "reload-committed":
          return;
        case "workspace-blocked":
          setUpdateReloadError(
            "This update is ready, but at least one local workspace change could not be verified in browser storage. Keep this tab open; LocateNow Pro will not reload."
          );
          break;
        case "unreachable":
          setUpdateReloadError(
            "The update could not be verified right now. Check your connection and try again."
          );
          break;
        case "changed":
          setUpdateReloadError(
            "Another deployment became available while checking. Review the updated notice and try again."
          );
          break;
        case "current":
          setStaleBuild(null);
          break;
        case "already-attempted":
          setUpdateReloadError(
            "This tab already tried to load this update, so an automatic reload loop was blocked. Save open form fields and reload the tab manually when ready."
          );
          break;
        case "attempt-storage-unavailable":
          setUpdateReloadError(
            "This browser cannot safely record the reload attempt, so LocateNow Pro will not risk a reload loop. Save open form fields and reload the tab manually when ready."
          );
          break;
      }
    } finally {
      setUpdateReloading(false);
    }
  };
  const [verifiedUserRole, setVerifiedUserRole] = React.useState<
    "agent" | "admin" | "manager" | "demo"
  >("agent");
  const [verifiedIsDemo, setVerifiedIsDemo] = React.useState(false);
  const [roleOwnerUserId, setRoleOwnerUserId] = React.useState<string | null>(
    null
  );
  const [roleLoading, setRoleLoading] = React.useState(true);
  const [roleLoadError, setRoleLoadError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] =
    React.useState<null | HTMLElement>(null);
  const {
    data: notificationsData,
    isError: notificationsError,
    isLoading: notificationsLoading,
    dataUpdatedAt: notificationsUpdatedAt,
    refetch: refetchNotifications,
  } = useQuery({
    queryKey: ["notifications", user?.id ?? null],
    queryFn: () => fetchWithAuth<{
      notifications: Array<{ id: string; type: string; visibility: string; title: string; message: string | null; entity_type: string | null; entity_id: string | null; created_at: string; is_read: boolean }>;
      unread_count: number;
      total: number;
    }>("/notifications?limit=15"),
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
    enabled: !!user,
  });
  React.useEffect(() => {
    return subscribeAppResume(() => {
      if (
        user &&
        (notificationsUpdatedAt === 0 ||
          Date.now() - notificationsUpdatedAt >= 60_000)
      ) {
        void refetchNotifications();
      }
    });
  }, [notificationsUpdatedAt, refetchNotifications, user]);
  const notifications = notificationsData?.notifications || [];
  // Lazy initializers read URL synchronously so the very first paint lands on
  // the correct page/client — avoids a dashboard→search flash on tab return.
  const [currentPage, setCurrentPage] = React.useState<string>(
    () => readCurrentNavigationState().page
  );
  const [selectedStatusFilter, setSelectedStatusFilter] = React.useState<
    string | null
  >(() => readCurrentNavigationState().status);
  const [selectedDrawerTab, setSelectedDrawerTab] = React.useState<
    string | null
  >(() => readCurrentNavigationState().drawerTab);
  const [selectedClientsTab, setSelectedClientsTab] =
    React.useState<ClientMainTab | null>(
      () => readCurrentNavigationState().clientsTab
    );
  const [initialNeighborhoods, setInitialNeighborhoods] = React.useState<
    string[] | null
  >(null);
  const [initialSearchCriteria, setInitialSearchCriteria] =
    React.useState<DashboardSearchCriteria | null>(null);
  const [selectedClientId, setSelectedClientId] = React.useState<string | null>(
    () => readCurrentNavigationState().clientId
  );
  const [propertyOpenRequest, setPropertyOpenRequest] = React.useState<{
    id: string;
    key: number;
  } | null>(() => {
    const id = readCurrentNavigationState().properties?.selectedPropertyId;
    return id ? { id, key: Date.now() } : null;
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const [persistenceClearRevision, setPersistenceClearRevision] =
    React.useState(0);
  // Bumped on every navigation that carries a settings deep-link so
  // re-clicking the same menu item (e.g. "Email Templates" while already in
  // Settings) still re-applies the tab.
  const [settingsTabRequestKey, setSettingsTabRequestKey] = React.useState(0);
  const [settingsDefaultTab, setSettingsDefaultTab] =
    React.useState<SettingsTabKey>(DEFAULT_SETTINGS_TAB);
  const [janeClientId, setJaneClientId] = React.useState<string | null>(null);
  const [demoViewKey, setDemoViewKey] = React.useState(0);
  const [demoResetNotice, setDemoResetNotice] = React.useState<string | null>(
    null
  );

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const capabilitiesVerified =
    Boolean(user) && roleOwnerUserId === user?.id;
  const userRole = capabilitiesVerified ? verifiedUserRole : "agent";
  const isDemo = capabilitiesVerified ? verifiedIsDemo : false;
  const isAdmin = userRole === "admin";
  const isManager = userRole === "manager";
  const isAgent = userRole === "agent";
  const canAccessAdmin = canAccessAdminSurface(userRole, isDemo);
  const canReviewContent = canAccessAdminContent(
    userRole,
    isDemo,
    CONTENT_REVIEW_ENABLED
  );
  // Elevated = admin OR manager (Grant 2026-04-25 manager-elevation).
  // Use for inventory edit + lead-processing surfaces. NOT for Admin
  // Dashboard route (admin-only) or System Guide tab (admin-only).
  const isElevated = isAdmin || isManager;
  const canAccessInsightsPage = !isDemo && !isAgent;
  // Messages surface: same D9 boundary as the clients Messages tab —
  // manager/admin only (the API 403s agents regardless).
  const canUseMessages = isElevated && !isDemo;

  const navItems = React.useMemo(() => {
    let items = [...baseNavItems];
    if (isDemo) {
      items = items.filter((item) => item.id !== "insights" && item.id !== "settings");
    } else if (isAgent) {
      // Keep agent navigation focused; insights is exposed inline from Properties.
      items = items.filter((item) => item.id !== "insights");
    }
    if (canUseMessages) {
      const clientsIdx = items.findIndex((item) => item.id === "clients");
      items.splice(clientsIdx + 1, 0, {
        id: "messages",
        label: "Messages",
        icon: <ForumIcon />,
      });
    }
    if (canAccessAdmin) {
      const adminEntries = [
        ...(canReviewContent
          ? [{ id: "content", label: "Socials", icon: <ContentIcon /> }]
          : []),
        { id: "admin", label: "Admin", icon: <AdminPanelSettingsIcon /> },
      ];
      items.splice(
        items.length - 1,
        0,
        ...adminEntries
      );
    }
    return items;
  }, [canAccessAdmin, canReviewContent, canUseMessages, isAgent, isDemo]);

  // Mobile bottom nav (Instagram-style pill): the 4 always-on destinations
  // live in the pill; everything else (Insights / Settings / Admin) moves to
  // the "More" sheet — the repurposed Drawer. Desktop top nav is unchanged.
  const primaryNavItems = React.useMemo(
    () => navItems.filter((item) => PRIMARY_NAV_IDS.includes(item.id)),
    [navItems]
  );
  const overflowNavItems = React.useMemo(
    () => navItems.filter((item) => !PRIMARY_NAV_IDS.includes(item.id)),
    [navItems]
  );

  // URL params are read synchronously in the lazy useState initializers above
  // so first paint already matches the URL. No post-mount swap needed.

  const isCurrentAuthGeneration = React.useCallback(
    (userId: string, generation: number) =>
      persistenceUserRef.current === userId &&
      authGenerationRef.current === generation,
    []
  );

  const loadUserRole = React.useCallback(
    async (
      userId: string,
      userEmail: string | null | undefined,
      generation: number,
      blocking: boolean
    ) => {
      roleRequestAbortRef.current?.abort();
      const requestAbort = new AbortController();
      roleRequestAbortRef.current = requestAbort;
      const requestEpoch = roleRequestEpochRef.current + 1;
      roleRequestEpochRef.current = requestEpoch;
      const isCurrentRequest = () =>
        isCurrentAuthGeneration(userId, generation) &&
        roleRequestEpochRef.current === requestEpoch;
      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("role, access_scope")
          .eq("id", userId)
          .abortSignal(requestAbort.signal)
          .maybeSingle();

        const normalizedEmail = (userEmail || "").trim().toLowerCase();
        const isDemoFallback = normalizedEmail === DEMO_FALLBACK_EMAIL;
        if (error && !isDemoFallback) throw error;
        if (!isCurrentRequest()) return;

        const role = profile?.role;
        const sandbox = profile?.access_scope === "sandbox" || isDemoFallback;
        setVerifiedIsDemo(sandbox || role === "demo");

        if (
          role === "admin" ||
          role === "manager" ||
          role === "agent" ||
          role === "demo"
        ) {
          setVerifiedUserRole(role);
        } else if (isDemoFallback) {
          setVerifiedUserRole("demo");
        } else {
          setVerifiedUserRole("agent");
        }
        roleOwnerUserIdRef.current = userId;
        setRoleOwnerUserId(userId);
        setRoleLoadError(null);
      } catch (error) {
        if (requestAbort.signal.aborted) return;
        console.error("Error loading user role:", error);
        if (isCurrentRequest()) {
          setRoleLoadError(
            "We could not verify your permissions. Your location is preserved; retry when the connection recovers."
          );
        }
      } finally {
        if (roleRequestAbortRef.current === requestAbort) {
          roleRequestAbortRef.current = null;
        }
        if (blocking && isCurrentRequest()) {
          setRoleLoading(false);
        }
      }
    },
    [isCurrentAuthGeneration]
  );

  const hydrateUserContact = React.useCallback(
    async (
      currentUser: User,
      generation: number,
      contactIdentity: EmailConfigIdentity
    ) => {
      let fullName = "";
      let phone = "";
      let jobTitle = "";
      try {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("first_name,last_name,phone,job_title")
          .eq("id", currentUser.id)
          .single();
        fullName = `${profileData?.first_name ?? ""} ${
          profileData?.last_name ?? ""
        }`.trim();
        phone = profileData?.phone || "";
        jobTitle = profileData?.job_title || "";
      } catch {
        // Fall back to saved/derived values below.
      }

      if (!isCurrentAuthGeneration(currentUser.id, generation)) return;
      emailService.hydrateDefaults(contactIdentity, {
        agentName:
          fullName ||
          getUserDisplayName(currentUser.email || ""),
        agentEmail: currentUser.email || "",
        agentPhone: phone,
        jobTitle,
      });
    },
    [isCurrentAuthGeneration]
  );

  React.useEffect(() => {
    if (!user) return;
    const removeStaleLegacyPayload = (key: string, raw: string | null) => {
      if (!raw || getPersistenceGeneration(user.id) === 0) return;
      const isActivePayload = isActiveUserPayloadKey(key, user.id);
      const isLegacy = (LEGACY_LOCAL_DATA_KEYS as readonly string[]).includes(key);
      if (!isActivePayload && !isLegacy) return;
      if (
        isLegacy ||
        !activeUserPayloadMatchesGeneration(
          key,
          raw,
          user.id,
          getPersistenceGeneration(user.id),
          clientStore.generation(key)
        )
      ) {
        clientStore.removeItem(key);
      }
    };
    for (const key of clientStore.keys()) {
      removeStaleLegacyPayload(key, clientStore.getItem(key));
    }
    return clientStore.subscribe((key, raw) => {
      removeStaleLegacyPayload(key, raw);
      if (key !== PERSISTENCE_LOCAL_CLEAR_KEY || !raw) return;
      const clearEvent = readPersistenceEvent(raw, "local-clear");
      if (clearEvent?.userId !== user.id) return;
      // URL-owned surface state is another browser persistence channel. Strip
      // filters/selections/deep-link remnants in every open tab so a verified
      // storage clear cannot repaint them on the next navigation or reload.
      const clearedUrl = clearUserNavigationPersistence(window.location.href);
      if (clearedUrl !== window.location.href) {
        window.history.replaceState({}, "", clearedUrl);
      }
      setSelectedClientId(null);
      setSelectedDrawerTab(null);
      setSelectedStatusFilter(null);
      setSelectedClientsTab(null);
      setPropertyOpenRequest(null);
      setInitialNeighborhoods(null);
      setInitialSearchCriteria(null);
      setPersistenceClearRevision((revision) => revision + 1);
      walkthroughSessionStore.clear();
      void queryClient.cancelQueries();
      queryClient.clear();
      emailService.resetUserConfig();
      queueMicrotask(() => {
        const contactIdentity = emailService.setUserId(user.id);
        void hydrateUserContact(
          user,
          authGenerationRef.current,
          contactIdentity
        );
      });
    });
  }, [hydrateUserContact, queryClient, user]);

  React.useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Supabase awaits subscribers while session recovery holds its lock. Keep
      // this callback synchronous: copy the event, dispatch once, and return.
      dispatchAuthSignal({ event, session });
    });

    return () => subscription.unsubscribe();
  }, []);

  React.useEffect(() => {
    if (!authSignal) return;

    const currentUser = authSignal.session?.user ?? null;
    const previousUserId = persistenceUserRef.current;
    const nextUserId = currentUser?.id ?? null;
    const storedUserId = getActivePersistenceUser();
    const previousIdentityUserId = previousUserId ?? storedUserId;
    const roleLoadMode = roleLoadModeForAuthEvent(
      authSignal.event,
      previousUserId,
      nextUserId
    );

    if (
      previousUserId !== nextUserId ||
      (!nextUserId && Boolean(storedUserId))
    ) {
      const generation = authGenerationRef.current + 1;
      authGenerationRef.current = generation;
      roleRequestAbortRef.current?.abort();
      roleRequestAbortRef.current = null;
      roleRequestEpochRef.current += 1;
      roleOwnerUserIdRef.current = null;
      setRoleOwnerUserId(null);
      setVerifiedUserRole("agent");
      setVerifiedIsDemo(false);
      emailService.resetUserConfig();

      // Fence the old audience before publishing the new user to React. If the
      // clear waited for a layout effect, the new user's Notifications query
      // could start and then be removed along with A's cache.
      if (
        previousIdentityUserId &&
        previousIdentityUserId !== nextUserId
      ) {
        void queryClient.cancelQueries();
        queryClient.clear();
      }
      persistenceUserRef.current = transitionPersistenceAuth(
        previousIdentityUserId,
        nextUserId
      );

      if (currentUser) {
        activityService.setUserId(currentUser.id);
        const contactIdentity = emailService.setUserId(currentUser.id);
        setRoleLoadError(null);
        setRoleLoading(true);
        void loadUserRole(
          currentUser.id,
          currentUser.email,
          generation,
          true
        );
        void hydrateUserContact(currentUser, generation, contactIdentity);
      } else {
        setRoleLoadError(null);
        setRoleLoading(false);
      }
    } else if (
      currentUser &&
      roleLoadMode === "background" &&
      roleOwnerUserIdRef.current === currentUser.id
    ) {
      void loadUserRole(
        currentUser.id,
        currentUser.email,
        authGenerationRef.current,
        false
      );
    }

    setUser(currentUser);
    setLoading(false);
  }, [authSignal, hydrateUserContact, loadUserRole, queryClient]);

  const retryRoleLoad = React.useCallback(() => {
    if (!user) return;
    const blocking = roleOwnerUserIdRef.current !== user.id;
    if (blocking) setRoleLoading(true);
    setRoleLoadError("Retrying permission verification...");
    void loadUserRole(
      user.id,
      user.email,
      authGenerationRef.current,
      blocking
    );
  }, [loadUserRole, user]);

  // Load the signed-in user's profile photo for the top-right nav avatar
  // (profiles.avatar_url — the same field Settings uploads to). Keyed on the
  // user id so it refreshes on login/logout; falls back to initials when unset.
  React.useEffect(() => {
    const uid = user?.id;
    if (!uid) {
      setUserAvatarUrl(null);
      return;
    }
    let active = true;
    void (async () => {
      try {
        const { data } = await supabase
          .from("profiles")
          .select("avatar_url")
          .eq("id", uid)
          .single();
        if (active) setUserAvatarUrl(data?.avatar_url || null);
      } catch {
        if (active) setUserAvatarUrl(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [user?.id]);

  React.useEffect(() => {
    if (loading || roleLoading || roleLoadError) return;
    if (
      (currentPage === "admin" && !canAccessAdmin) ||
      (currentPage === "content" && !canReviewContent)
    ) {
      handleNavigate("dashboard");
    }
    // NOTE: demo may open Settings — the page is demo-aware (tabs disabled,
    // "(Demo)" labels). The old redirect here made the account-menu links
    // silently bounce to Dashboard in demo.
    if (!canAccessInsightsPage && currentPage === "insights") {
      handleNavigate("dashboard");
    }
  }, [loading, roleLoading, roleLoadError, currentPage, canAccessAdmin, canReviewContent, canAccessInsightsPage]);

  const refreshJaneClientId = React.useCallback(async () => {
    if (!isDemo || !user) {
      setJaneClientId(null);
      return null;
    }

    const { data, error } = await supabase
      .from("clients")
      .select("id")
      .eq("first_name", "Jane")
      .eq("last_name", "Demo")
      .limit(1)
      .single();

    if (error) {
      console.error("Error loading Jane Demo client ID:", error);
      setJaneClientId(null);
      return null;
    }

    const nextId = data?.id ?? null;
    setJaneClientId(nextId);
    return nextId;
  }, [isDemo, user]);

  React.useEffect(() => {
    void refreshJaneClientId();
  }, [refreshJaneClientId]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
    if (
      notificationsUpdatedAt === 0 ||
      Date.now() - notificationsUpdatedAt >= 60_000
    ) {
      void refetchNotifications();
    }
  };
  const handleClose = () => setAnchorEl(null);
  const handleNotificationClose = () => setNotificationAnchor(null);

  const clearDemoSessionState = (clearWalkthroughCompletion = false) => {
    try {
      // Search drafts live in clientStore (localStorage) now — clear them there.
      clearUserPersistence(user?.id ?? null);
      // The demo-walkthrough flag stays a session-only UX flag (sessionStorage).
      if (clearWalkthroughCompletion) {
        walkthroughSessionStore.clear();
      }
    } catch {
      /* fail silently */
    }
  };

  const handleLogout = async () => {
    try {
      if (isDemo) {
        clearDemoSessionState(true);
        let serverResetApplied = false;
        if (!isLocalhostHost()) {
          try {
            await fetchWithAuth("/admin/reset-demo", { method: "POST" });
            serverResetApplied = true;
          } catch (error) {
            console.warn(
              "Demo reset failed during logout, falling back to local reset:",
              error
            );
          }
        }

        if (!serverResetApplied) {
          const demoJaneId = janeClientId ?? (await refreshJaneClientId());
          if (demoJaneId) {
            await supabase
              .from("clients")
              .update({
                status: "new_client",
                last_contacted_at: new Date(
                  Date.now() - 10 * 24 * 60 * 60 * 1000
                ).toISOString(),
              })
              .eq("id", demoJaneId);
          }
        }
      } else {
        // Drafts are in clientStore (localStorage) — clear them there on logout
        // so the next user on this device doesn't inherit them.
        clearUserPersistence(user?.id ?? null);
      }
    } catch { /* fail silently */ }
    await supabase.auth.signOut();
    handleClose();
  };

  const handleNavigate = (
    page: string,
    clientId?: string,
    params?: {
      status?: ClientNavigationStatus;
      tab?: string;
      clientsTab?: ClientMainTab;
      neighborhoods?: string[];
      searchCriteria?: DashboardSearchCriteria | null;
      propertyId?: string;
      settingsTab?: SettingsTabKey;
    }
  ) => {
    const targetPage =
      (page === "admin" && !canAccessAdmin) ||
      (page === "content" && !canReviewContent) ||
      (page === "messages" && !canUseMessages)
      ? "dashboard"
      : page;
    const currentNavigation = readCurrentNavigationState();
    const targetPropertyState = params?.propertyId
      ? buildSelectedPropertyNavigationState(
          currentNavigation.properties,
          params.propertyId
        )
      : null;
    setCurrentPage(targetPage);
    setSelectedClientId(clientId || null);
    setSelectedStatusFilter(params?.status || null);
    setSelectedDrawerTab(params?.tab || null);
    setSelectedClientsTab(params?.clientsTab || null);
    setInitialNeighborhoods(params?.neighborhoods || null);
    setInitialSearchCriteria(params?.searchCriteria || null);
    setSettingsDefaultTab(params?.settingsTab ?? DEFAULT_SETTINGS_TAB);
    if (params?.settingsTab) {
      setSettingsTabRequestKey((k) => k + 1);
    }
    setPropertyOpenRequest(
      targetPropertyState?.selectedPropertyId
        ? { id: targetPropertyState.selectedPropertyId, key: Date.now() }
        : null
    );
    setMobileDrawerOpen(false);

    const nextUrl = writeAppNavigationState(window.location.href, {
      page: targetPage,
      clientId: clientId ?? null,
      drawerTab: params?.tab ?? null,
      status: params?.status ?? null,
      clientsTab: params?.clientsTab ?? null,
      ...(targetPropertyState ? { properties: targetPropertyState } : {}),
    });
    if (nextUrl !== window.location.href) window.history.pushState({}, "", nextUrl);
  };

  React.useEffect(() => {
    const handlePopState = () => {
      // R2/N8: the URL-derived fields (including the drawer tab, which was
      // hardcoded to null here) are applied by a tested pure function in
      // lib/appNavigationState — App itself is not renderable in the suite.
      applyPopStateNavigation(readCurrentNavigationState(), {
        setPage: setCurrentPage,
        setClientId: setSelectedClientId,
        setStatus: setSelectedStatusFilter,
        setDrawerTab: setSelectedDrawerTab,
        setClientsTab: setSelectedClientsTab,
        setPropertyOpenRequest,
      });
      // Purely local surface state, not derived from the URL.
      setInitialNeighborhoods(null);
      setInitialSearchCriteria(null);
      setSettingsDefaultTab(DEFAULT_SETTINGS_TAB);
      setMobileDrawerOpen(false);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const visibleDrawerTab =
    capabilitiesVerified && !canUseMessages && selectedDrawerTab === "messages"
      ? "timeline"
      : selectedDrawerTab;

  React.useLayoutEffect(() => {
    if (
      currentPage !== "clients" ||
      !selectedClientId ||
      !capabilitiesVerified ||
      canUseMessages ||
      selectedDrawerTab !== "messages"
    ) {
      return;
    }
    // The Messages drawer tab is manager/admin-only. Replace the unavailable
    // location instead of pushing, otherwise Back would loop into the same URL
    // lie for an agent or a newly downgraded manager.
    const nextUrl = writeAppNavigationState(window.location.href, {
      page: "clients",
      clientId: selectedClientId,
      drawerTab: "timeline",
    });
    window.history.replaceState(window.history.state, "", nextUrl);
    setSelectedDrawerTab("timeline");
  }, [
    canUseMessages,
    capabilitiesVerified,
    currentPage,
    selectedClientId,
    selectedDrawerTab,
  ]);

  const resetDemoExperience = async () => {
    if (!isDemo) return;

    setDemoResetNotice(null);

    let serverResetApplied = false;
    if (!isLocalhostHost()) {
      try {
        await fetchWithAuth("/admin/reset-demo", { method: "POST" });
        serverResetApplied = true;
      } catch (error) {
        setDemoResetNotice(demoResetFailureMessage(error));
        // B4 (PROVIDER-REVIEW-PR339): a DELIBERATE refusal must leave everything
        // alone. Returning here is the fix.
        //
        // Previously every failure fell through to the local replay, so a 409
        // produced an amber "reset refused — immutable history was preserved"
        // banner sitting on top of a dashboard that had visibly reset. Worse, the
        // replay's `status` UPDATE fires 054's and 060's triggers and APPENDS to
        // the very ledgers the message claims were left untouched — so the
        // refusal path was itself creating durable history.
        //
        // A transport error is different and still falls through: server state is
        // unknown there, and the local replay is the intended demo-continuity
        // path. A refusal is not an error about reachability; it is an answer.
        if (isDemoResetRefusal(error)) {
          console.warn(
            "Demo reset refused by the server; leaving demo state untouched:",
            error
          );
          return;
        }
        console.warn(
          "Demo reset failed, falling back to local demo reset:",
          error
        );
      }
    }

    // Only safe once the server has either succeeded or failed in a way that
    // makes a local replay the right answer. Clearing this BEFORE the call meant
    // a refusal had already discarded local state before it was told no.
    clearDemoSessionState(false);

    const demoJaneId = serverResetApplied
      ? await refreshJaneClientId()
      : janeClientId ?? (await refreshJaneClientId());

    if (!serverResetApplied && demoJaneId) {
      const { error } = await supabase
        .from("clients")
        .update({
          status: "new_client",
          last_contacted_at: new Date(
            Date.now() - 10 * 24 * 60 * 60 * 1000
          ).toISOString(),
        })
        .eq("id", demoJaneId);
      if (error) {
        console.warn(
          "Failed to restore Jane Demo to New Client during replay reset:",
          error
        );
      }
    }

    await queryClient.invalidateQueries();
    handleNavigate("dashboard");
    setDemoViewKey((current) => current + 1);
  };

  const writeClientDrawerLocation = (
    clientId: string | null,
    drawerTab: string | null
  ) => {
    setSelectedClientId(clientId);
    setSelectedDrawerTab(drawerTab);
    const nextUrl = writeAppNavigationState(window.location.href, {
      page: "clients",
      clientId,
      drawerTab,
    });
    if (nextUrl !== window.location.href) {
      window.history.pushState(window.history.state, "", nextUrl);
    }
  };

  const handleClientDrawerOpen = (clientId: string, tab = "timeline") =>
    writeClientDrawerLocation(clientId, tab);

  const handleClientDrawerTabChange = (tab: string) => {
    const clientId =
      selectedClientId ?? readCurrentNavigationState().clientId;
    if (clientId) writeClientDrawerLocation(clientId, tab);
  };

  const handleClientDrawerClose = () =>
    writeClientDrawerLocation(null, null);

  const getUserInitials = (email: string) => {
    return email
      .split("@")[0]
      .split(".")
      .map((part) => part.charAt(0).toUpperCase())
      .join("")
      .slice(0, 2);
  };

  const unreadCount = notificationsData?.unread_count ?? 0;

  // Loading
  if (loading || (user && roleLoading)) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            background: theme.palette.background.default,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: 3,
              background: gradients.primary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <ApartmentIcon sx={{ fontSize: 32, color: "white" }} />
          </Box>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading LocateNow Pro...
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  // Login
  if (!user) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Login />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <WalkthroughProvider>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          // Clear the floating bottom nav on mobile so the last card/footer
          // is never hidden behind the pill (incl. iOS safe area).
          pb: isMobile ? "calc(env(safe-area-inset-bottom, 0px) + 88px)" : 0,
        }}
      >
        {/* App Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: "blur(20px)",
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar sx={{ gap: 2 }}>
            {/* Hamburger removed — mobile navigation lives in the floating
                bottom pill (MobileBottomNav). Insights/Admin/Settings are
                reached via the Properties/Dashboard header buttons + the
                account menu. */}

            {/* Logo — one mark, both modes: the white wordmark on a rounded
                rectangle (chip radius). Light = the brand blue the client
                link uses; dark = the same purple the old icon circle wore.
                (Grant 2026-07-11) */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 36,
                  px: 1.75,
                  borderRadius: "8px",
                  ...(themeMode === "light"
                    ? { bgcolor: "primary.main" }
                    : { background: gradients.primary }),
                }}
              >
                <Box
                  component="img"
                  src={COMPANY_LOGO_WHITE_URL}
                  alt="LocateNow"
                  sx={{ display: "block", height: 24, width: "auto" }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  display: { xs: "none", sm: "block" },
                }}
              >
                Pro
              </Typography>
            </Box>

            {/* Desktop Nav */}
            {!isMobile && (
              <Box sx={{ display: "flex", ml: 4, gap: 1, ...(isDemo && { flex: 1, justifyContent: "center" }) }}>
                {navItems.map((item) => (
                  <Button
                    key={item.id}
                    startIcon={item.icon}
                    onClick={() => handleNavigate(item.id)}
                    sx={{
                      px: 2,
                      color:
                        (["search", "insights"].includes(currentPage) ? "properties" : currentPage) === item.id
                          ? "primary.main"
                          : "text.secondary",
                      backgroundColor:
                        (["search", "insights"].includes(currentPage) ? "properties" : currentPage) === item.id
                          ? alpha(theme.palette.primary.main, 0.1)
                          : "transparent",
                      fontWeight: (["search", "insights"].includes(currentPage) ? "properties" : currentPage) === item.id ? 600 : 400,
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          0.08
                        ),
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            )}

            {/* Spacer: logo stays left, bell + avatar right. On desktop demo
                the nav block centers itself (flex: 1), so skip the spacer
                there only. */}
            {(isMobile || !isDemo) && <Box sx={{ flexGrow: 1 }} />}

            {/* Notifications */}
            <Tooltip
              title={
                notificationsError
                  ? "Couldn't read notifications"
                  : "Notifications"
              }
            >
              <IconButton onClick={handleNotificationClick}>
                <NotificationBellBadge
                  unreadCount={unreadCount}
                  loading={notificationsLoading}
                  error={notificationsError}
                />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={notificationAnchor}
              open={Boolean(notificationAnchor)}
              onClose={handleNotificationClose}
              PaperProps={{ sx: { width: 320, maxHeight: 400 } }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Notifications
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleNotificationClose}
                  sx={{
                    color: "text.primary",
                    p: 0.5,
                    "&:hover": { backgroundColor: alpha(theme.palette.text.primary, 0.08) },
                  }}
                  aria-label="Close notifications"
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
              <Divider />
              {notificationsLoading || notificationsError || notifications.length === 0 ? (
                <NotificationMenuState
                  loading={notificationsLoading}
                  error={notificationsError}
                  onRetry={() => { void refetchNotifications(); }}
                />
              ) : (
                notifications.map((notification) => (
                  <MenuItem
                    key={notification.id}
                    onClick={async () => {
                      if (!notification.is_read) {
                        try {
                          await fetchWithAuth(`/notifications/${notification.id}/read`, { method: "POST" });
                          refetchNotifications();
                        } catch {}
                      }
                      handleNotificationClose();
                      // Navigate based on entity_type
                      if (notification.entity_type === "client" && notification.entity_id) {
                        handleNavigate("clients", notification.entity_id);
                      } else if (notification.entity_type === "system") {
                        handleNavigate("admin");
                      }
                    }}
                    sx={{
                      py: 1.5,
                      whiteSpace: "normal",
                      backgroundColor: !notification.is_read
                        ? alpha(theme.palette.primary.main, 0.04)
                        : "transparent",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: !notification.is_read ? 600 : 400 }}
                      >
                        {notification.title}
                      </Typography>
                      {notification.message && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {notification.message.slice(0, 80)}{notification.message.length > 80 ? "..." : ""}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {(() => {
                          const d = new Date(notification.created_at);
                          const now = new Date();
                          const diff = Math.floor((now.getTime() - d.getTime()) / 60000);
                          if (diff < 1) return "Just now";
                          if (diff < 60) return `${diff}m ago`;
                          if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
                          return `${Math.floor(diff / 1440)}d ago`;
                        })()}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
              {unreadCount > 0 && (
                <>
                  <Divider />
                  <MenuItem
                    onClick={async () => {
                      try {
                        await fetchWithAuth("/notifications/read-all", { method: "POST" });
                        refetchNotifications();
                      } catch {}
                      handleNotificationClose();
                    }}
                    sx={{ justifyContent: "center" }}
                  >
                    <Typography variant="body2" color="primary">
                      Mark all as read
                    </Typography>
                  </MenuItem>
                </>
              )}
            </Menu>

            {/* User Menu */}
            <Tooltip title="Account">
              <IconButton onClick={handleMenu}>
                <Avatar
                  src={userAvatarUrl || undefined}
                  alt={user.email || "Account"}
                  sx={{
                    width: 36,
                    height: 36,
                    background: gradients.primary,
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                >
                  {getUserInitials(user.email || "")}
                </Avatar>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{ sx: { width: 220, mt: 1 } }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {getUserDisplayName(user.email || "")}
                  {isDemo && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        ml: 1,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: "warning.light",
                        color: "warning.contrastText",
                        fontWeight: 600,
                      }}
                    >
                      Demo
                    </Typography>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleNavigate("settings", undefined, {
                    settingsTab: "profile",
                  });
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <AccountIcon fontSize="small" />
                </ListItemIcon>
                My Profile
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleNavigate("settings");
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Settings
              </MenuItem>
              <MenuItem onClick={toggleThemeMode}>
                <ListItemIcon>
                  {themeMode === "dark" ? (
                    <LightModeIcon fontSize="small" />
                  ) : (
                    <DarkModeIcon fontSize="small" />
                  )}
                </ListItemIcon>
                {themeMode === "dark" ? "Light mode" : "Dark mode"}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleNavigate("settings", undefined, {
                    settingsTab: "templates",
                  });
                  handleClose();
                }}
              >
                <ListItemIcon>
                  <EmailIcon fontSize="small" />
                </ListItemIcon>
                Email Templates
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                Sign Out
              </MenuItem>
            </Menu>
          </Toolbar>
          {/* Demo banner — inside AppBar so it sticks on scroll */}
          <Collapse in={isDemo && !roleLoading}>
            <Box
              data-tour="demo-banner"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 0.75,
                px: 2,
                background: "linear-gradient(90deg, rgba(245,158,11,0.12) 0%, rgba(245,158,11,0.06) 100%)",
                borderTop: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                <Box component="span" sx={{ color: "#F59E0B", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  Demo Mode
                </Box>
                <Box component="span" sx={{ color: "text.secondary" }}>
                  {" "}— exploring with sample data
                </Box>
              </Typography>
              <DemoBannerTourButton
                janeClientId={janeClientId}
                prepareWalkthrough={resetDemoExperience}
              />
            </Box>
          </Collapse>
        </AppBar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          PaperProps={{ sx: { width: 280 } }}
        >
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  background: gradients.primary,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ApartmentIcon sx={{ fontSize: 20, color: "white" }} />
              </Box>
              <Typography variant="h6" fontWeight={700}>
                LocateNow Pro
              </Typography>
            </Box>
            <IconButton onClick={() => setMobileDrawerOpen(false)}>
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <Divider />
          <List sx={{ px: 1, py: 2 }}>
            {/* Overflow only — primary tabs live in the bottom pill */}
            {overflowNavItems.map((item) => (
              <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => handleNavigate(item.id)}
                  selected={(["search", "insights"].includes(currentPage) ? "properties" : currentPage) === item.id}
                  sx={{
                    borderRadius: 2,
                    "&.Mui-selected": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color:
                        (["search", "insights"].includes(currentPage) ? "properties" : currentPage) === item.id
                          ? "primary.main"
                          : "text.secondary",
                      minWidth: 40,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: (["search", "insights"].includes(currentPage) ? "properties" : currentPage) === item.id ? 600 : 400,
                      color:
                        (["search", "insights"].includes(currentPage) ? "properties" : currentPage) === item.id
                          ? "primary.main"
                          : "text.primary",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Drawer>

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            maxWidth: 1600,
            width: "100%",
            mx: "auto",
          }}
        >
          <Collapse in={Boolean(staleBuild)}>
            <Alert
              severity={updateReloadError ? "warning" : "info"}
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={updateReloading}
                  onClick={() => { void handleReloadUpdate(); }}
                >
                  {updateReloading ? "Checking..." : "Reload update"}
                </Button>
              }
              sx={{
                mb: 2,
                alignItems: "flex-start",
                "& .MuiAlert-message": { minWidth: 0, flex: 1 },
                "& .MuiAlert-action": { alignSelf: "center", flexShrink: 0 },
              }}
              data-testid="app-update-ready"
            >
              <Typography variant="body2" fontWeight={600}>
                A newer LocateNow Pro version is ready.
              </Typography>
              <Typography variant="body2">
                Save any open form fields first. Registered workspace state will
                be durably verified before reload; unsaved fields outside that
                workspace may be lost.
              </Typography>
              {updateReloadError && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {updateReloadError}
                </Typography>
              )}
            </Alert>
          </Collapse>
          <Collapse in={storageDurability.mode === "memory-only"}>
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="local-durability-warning">
              {storageDurability.reason === "oversize"
                ? "One local workspace item was too large to save. Your current tab still has it, but it will not survive a reload."
                : storageDurability.reason === "invalid"
                ? "One local workspace item failed validation and was not saved. Your current tab still has it, but it will not survive a reload."
                : "Browser storage is unavailable or full. Local changes are being kept only in this tab and will not survive a reload."}
            </Alert>
          </Collapse>
          <Collapse in={Boolean(roleLoadError)}>
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={retryRoleLoad}>
                  Retry
                </Button>
              }
              sx={{ mb: 2 }}
            >
              {roleLoadError}
            </Alert>
          </Collapse>
          <Collapse in={Boolean(demoResetNotice)}>
            <Alert
              severity="warning"
              onClose={() => setDemoResetNotice(null)}
              sx={{ mb: 2 }}
            >
              {demoResetNotice}
            </Alert>
          </Collapse>
          {currentPage === "dashboard" && (
            <ApartmentDashboard
              key={`dashboard-${isDemo ? demoViewKey : 0}-${persistenceClearRevision}`}
              onNavigate={handleNavigate}
              isDemo={isDemo}
              userRole={userRole}
              canAccessAdmin={canAccessAdmin}
              canReviewContent={canReviewContent}
            />
          )}
          {currentPage === "clients" && (
            <ClientList
              key={`clients-${isDemo ? demoViewKey : 0}-${persistenceClearRevision}`}
              onNavigate={handleNavigate}
              initialSocialSource={socialClientSource}
              onSocialSourceConsumed={() => setSocialClientSource(null)}
              initialSelectedClientId={selectedClientId}
              initialStatusFilter={selectedStatusFilter}
              initialDrawerTab={visibleDrawerTab}
              initialMainTab={selectedClientsTab}
              onClientDrawerOpen={handleClientDrawerOpen}
              onClientDrawerTabChange={handleClientDrawerTabChange}
              onClientDrawerClose={handleClientDrawerClose}
              isDemo={isDemo}
            />
          )}
          {/* Settings renders in demo too — the component is demo-aware
              (tabs disabled + "(Demo)" labels), so the menu links always
              land somewhere real instead of silently doing nothing. */}
          {currentPage === "settings" && (
            <Settings
              key={`settings-${user?.id ?? "none"}`}
              identityUserId={user?.id ?? ""}
              isAdmin={isAdmin}
              isDemo={isDemo}
              defaultTab={settingsDefaultTab}
              defaultTabRequestKey={settingsTabRequestKey}
            />
          )}
          {["properties", "search", "insights"].includes(currentPage) && (
            <PropertiesShell page={currentPage} onNavigate={handleNavigate} canAccessInsights={canAccessInsightsPage}>
          {currentPage === "properties" && (
            <PropertiesPage
              key={`properties-${persistenceClearRevision}`}
              // Elevated (admin OR manager) — managers can edit property
              // contact info per Grant 2026-04-25 manager-elevation.
              isAdmin={isElevated}
              isDemo={isDemo}
              userRole={userRole}
              initialPropertyId={propertyOpenRequest?.id ?? null}
              initialPropertyRequestKey={propertyOpenRequest?.key ?? 0}
              onNavigate={canAccessInsightsPage ? handleNavigate : undefined}
            />
          )}
          {currentPage === "insights" && canAccessInsightsPage && (
            <MarketInsightsPage onNavigate={handleNavigate} />
          )}
          {currentPage === "search" && (
            <ApartmentSearch
              userId={user?.id ?? null}
              key={`search-${isDemo ? demoViewKey : 0}-${persistenceClearRevision}`}
              initialClientId={selectedClientId}
              initialNeighborhoods={initialNeighborhoods}
              initialSearchCriteria={initialSearchCriteria}
              // Pass elevated (admin OR manager) so manager sees the
              // force-refresh-stale-property action + edits — Grant directive
              // 2026-04-25 manager-elevation. Admin-only Settings tabs stay
              // gated by the actual isAdmin (passed below to <Settings>).
              isAdmin={isElevated}
              isDemo={isDemo}
              userRole={userRole}
              onNavigate={handleNavigate}
            />
          )}
            </PropertiesShell>
          )}
          {currentPage === "messages" && canUseMessages && (
            <MessagesPage
              viewerId={user?.id}
              key={`messages-${persistenceClearRevision}`}
              selectedClientId={selectedClientId}
              onSelectClient={(clientId) =>
                handleNavigate("messages", clientId ?? undefined)
              }
              onOpenClientRecord={(clientId, tab) =>
                handleNavigate("clients", clientId, { tab: tab ?? "timeline" })
              }
              onNavigateToSettings={() =>
                handleNavigate("settings", undefined, { settingsTab: "integrations" })
              }
            />
          )}
          {currentPage === "admin" && canAccessAdmin && <AdminDashboard />}
          {currentPage === "content" && canReviewContent && <SocialsRoute userId={user?.id} key={`content-${user?.id ?? "none"}`} enabled={process.env.REACT_APP_SOCIALS_ENABLED === "true"} onTurnIntoLead={source => { setSocialClientSource(source); handleNavigate("clients"); }} />}
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 2,
            px: 3,
            mt: "auto",
            backgroundColor: alpha(theme.palette.background.paper, 0.5),
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            {copyrightLine()}
          </Typography>
        </Box>

        {/* Mobile bottom nav — Instagram-style floating pill (one-tap nav).
            4 tabs only (Grant 2026-07-11): Insights lives behind the
            Properties header button, Admin behind the Dashboard header
            button, Settings behind the account menu — no 5th slot. */}
        {isMobile && (
          <MobileBottomNav
            items={primaryNavItems}
            currentPage={currentPage}
            onNavigate={(page) => handleNavigate(page)}
            onMore={null}
          />
        )}
      </Box>
      {isDemo && !isMobile && (
        <WalkthroughOverlay
          navigateTo={handleNavigate}
          janeClientId={janeClientId}
        />
      )}
      </WalkthroughProvider>
    </ThemeProvider>
  );
};

export default AppRoot;
